import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  CONFIG_TEMPLATE,
  KIND_LABEL,
  KIND_LABEL_KEY,
  SCOPE_LABEL,
  SCOPE_LABEL_KEY,
  cardCategoriesFromText,
  useCreatePromotion,
  type PromotionKind,
  type PromotionScope,
} from '@/hooks/use-promotions';
import { useT } from '@/i18n/use-t';

const KIND_OPTIONS: PromotionKind[] = [
  'percentage_off_line',
  'fixed_off_line',
  'percentage_off_basket',
  'fixed_off_basket',
  'coupon',
  'time_based',
  'customer_tier',
  'fixed_price',
  'group_mix',
];

const SCOPE_OPTIONS: PromotionScope[] = ['line', 'basket', 'customer', 'tender'];

const DEFAULT_SCOPE_BY_KIND: Record<PromotionKind, PromotionScope> = {
  percentage_off_line: 'line',
  fixed_off_line: 'line',
  percentage_off_basket: 'basket',
  fixed_off_basket: 'basket',
  coupon: 'line',
  time_based: 'basket',
  customer_tier: 'customer',
  fixed_price: 'line',
  group_mix: 'line',
};

function extractError(err: unknown): string | null {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string; code?: string } | undefined;
    if (body?.message) return body.code ? `${body.code}: ${body.message}` : body.message;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return null;
}

export function PromotionNewPage() {
  const t = useT('admin_ui');
  const navigate = useNavigate();
  const create = useCreatePromotion();
  const [kind, setKind] = useState<PromotionKind>('percentage_off_line');
  const [scope, setScope] = useState<PromotionScope>('line');
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('100');
  const [couponCode, setCouponCode] = useState('');
  const [cardInclude, setCardInclude] = useState('');
  const [cardExclude, setCardExclude] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [configText, setConfigText] = useState(
    JSON.stringify(CONFIG_TEMPLATE.percentage_off_line, null, 2),
  );
  const [banner, setBanner] = useState<string | null>(null);

  const configParsed = useMemo(() => {
    try {
      return { ok: true as const, value: JSON.parse(configText) as Record<string, unknown> };
    } catch (err) {
      return { ok: false as const, error: (err as Error).message };
    }
  }, [configText]);

  function changeKind(next: PromotionKind) {
    setKind(next);
    setScope(DEFAULT_SCOPE_BY_KIND[next]);
    setConfigText(JSON.stringify(CONFIG_TEMPLATE[next], null, 2));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBanner(null);
    if (!configParsed.ok) {
      setBanner(
        `${t('promotions.new.configInvalid', 'Config JSON is invalid:')} ${configParsed.error}`,
      );
      return;
    }
    try {
      const created = await create.mutateAsync({
        code: code.trim(),
        displayName: displayName.trim(),
        description: description.trim() || undefined,
        scope,
        kind,
        config: configParsed.value,
        priority: priority ? Number(priority) : undefined,
        couponCode: couponCode.trim() || null,
        cardCategories: cardCategoriesFromText(cardInclude, cardExclude),
        onlineOnly,
      });
      navigate(`/promotions/${created.id}`);
    } catch (err) {
      setBanner(extractError(err) ?? t('promotions.new.createFailed', 'Create failed.'));
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <Link to="/promotions" className="text-xs text-muted-foreground hover:underline">
          {t('promotions.nav.back', '← Promotions')}
        </Link>
        <h1 className="text-2xl font-semibold">{t('promotions.new.title', 'New promotion')}</h1>
      </header>

      <form
        onSubmit={onSubmit}
        className="grid grid-cols-2 gap-4 rounded-md border border-border bg-card p-4"
      >
        <div>
          <Label htmlFor="kind">{t('promotions.field.kind', 'Kind')}</Label>
          <select
            id="kind"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={kind}
            onChange={(e) => changeKind(e.target.value as PromotionKind)}
          >
            {KIND_OPTIONS.map((k) => (
              <option key={k} value={k}>
                {t(KIND_LABEL_KEY[k], KIND_LABEL[k])}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="scope">{t('promotions.field.scope', 'Scope')}</Label>
          <select
            id="scope"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={scope}
            onChange={(e) => setScope(e.target.value as PromotionScope)}
          >
            {SCOPE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {t(SCOPE_LABEL_KEY[s], SCOPE_LABEL[s])}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label htmlFor="code">{t('promotions.field.code', 'Code')}</Label>
          <Input
            id="code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="BAKERY-10"
            required
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="displayName">{t('promotions.field.displayName', 'Display name')}</Label>
          <Input
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t('promotions.new.namePlaceholder', 'Bakery 10% off')}
            required
            className="mt-1"
          />
        </div>

        <div className="col-span-2">
          <Label htmlFor="description">
            {t('promotions.new.descriptionLabel', 'Description (optional)')}
          </Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="priority">
            {t('promotions.new.priorityLabel', 'Priority (higher = earlier)')}
          </Label>
          <Input
            id="priority"
            type="number"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="couponCode">
            {t('promotions.new.couponLabel', 'Coupon code (optional)')}
          </Label>
          <Input
            id="couponCode"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            placeholder={t('promotions.new.couponPlaceholder', '(optional)')}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="cardInclude">
            {t('promotions.new.cardIncludeLabel', 'Card categories — only for (optional)')}
          </Label>
          <Input
            id="cardInclude"
            value={cardInclude}
            onChange={(e) => setCardInclude(e.target.value)}
            placeholder="regular_bonus, vip"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="cardExclude">
            {t('promotions.new.cardExcludeLabel', 'Card categories — not for (optional)')}
          </Label>
          <Input
            id="cardExclude"
            value={cardExclude}
            onChange={(e) => setCardExclude(e.target.value)}
            placeholder="staff"
            className="mt-1"
          />
        </div>
        <p className="col-span-2 -mt-2 text-xs text-muted-foreground">
          {t(
            'promotions.new.cardHint',
            'Leave both empty for everyone. With either set, the promotion applies only when the card is confirmed online — offline it is skipped.',
          )}
        </p>

        <div className="col-span-2">
          <label htmlFor="onlineOnly" className="flex items-center gap-2 text-sm font-medium">
            <input
              id="onlineOnly"
              type="checkbox"
              checked={onlineOnly}
              onChange={(e) => setOnlineOnly(e.target.checked)}
            />
            {t('promotions.field.onlineOnly', 'Online only')}
          </label>
          <p className="mt-1 text-xs text-muted-foreground">
            {t(
              'promotions.field.onlineOnlyHint',
              'Applied only while the till is online. A self-checkout that is offline skips this promotion and prices the basket without it. Tills not yet updated ignore this setting and apply the promotion offline too.',
            )}
          </p>
        </div>

        <div className="col-span-2">
          <Label htmlFor="config">{t('promotions.field.config', 'Config (JSON)')}</Label>
          <Textarea
            id="config"
            value={configText}
            onChange={(e) => setConfigText(e.target.value)}
            rows={12}
            spellCheck={false}
            className="mt-1 font-mono text-xs"
          />
          {!configParsed.ok && (
            <p className="mt-1 text-xs text-destructive">
              {t('promotions.form.jsonParseError', 'JSON parse error:')} {configParsed.error}
            </p>
          )}
        </div>

        {banner && (
          <p className="col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {banner}
          </p>
        )}

        <div className="col-span-2 flex gap-2">
          <Button type="submit" disabled={create.isPending || !configParsed.ok}>
            {create.isPending
              ? t('promotions.new.creating', 'Creating…')
              : t('promotions.new.submit', 'Create promotion')}
          </Button>
          <Button asChild variant="ghost">
            <Link to="/promotions">{t('common.cancel', 'Cancel')}</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}
